import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/Page";
import { FolderLock, FileDown, ShieldCheck } from "lucide-react";

export default function ExecutedDocuments() {
  const navigate = useNavigate();
  const { data: rows } = useQuery({ queryKey: ["legal-executed"], queryFn: async () => (await api.get("/legal/executed")).data });

  const openDoc = async (docId) => {
    const res = await api.get(`/documents/${docId}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data]));
    window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  return (
    <div data-testid="executed-documents-page">
      <PageHeader title="Executed Documents" subtitle="Immutable repository of signed agreements with integrity checksums." />
      <div className="space-y-2">
        {(rows || []).length === 0 && (
          <p className="rounded-lg border border-dashed border-[#2D3F62] px-4 py-10 text-center text-sm text-slate-500" data-testid="no-executed">
            No executed documents yet. Open a contract and use "Upload Signed & Execute".
          </p>
        )}
        {(rows || []).map((e) => (
          <div key={e.id} className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-4" data-testid={`executed-${e.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 font-medium text-slate-100">
                  <ShieldCheck className="h-4 w-4 text-emerald-400" />
                  <span className="font-mono text-xs text-cyan-300">{e.contract_ref}</span> {e.customer_legal_name}
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  Signed {e.execution_date || "—"} · Effective {e.effective_date || "—"} · by {e.uploaded_by_name}
                </p>
                {e.signatories?.length > 0 && (
                  <p className="mt-1 text-xs text-slate-400">Signatories: {e.signatories.map((s) => `${s.name}${s.title ? " (" + s.title + ")" : ""}`).join(", ")}</p>
                )}
                <p className="mt-1 break-all font-mono text-[10px] text-slate-600">SHA-256: {e.checksum_sha256}</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => openDoc(e.document_id)} className="flex items-center gap-1 rounded-md border border-[#2D3F62] px-2.5 py-1.5 text-xs text-slate-200 hover:bg-[#17233B]" data-testid={`executed-download-${e.id}`}>
                  <FileDown className="h-3.5 w-3.5" /> {e.original_filename}
                </button>
                <button onClick={() => navigate(`/legal/contracts/${e.contract_id}`)} className="rounded-md border border-[#2D3F62] px-2.5 py-1.5 text-xs text-cyan-300 hover:bg-[#17233B]" data-testid={`executed-contract-${e.id}`}>Contract</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
